import { PawnState } from '@dreamgame/shared';
import { tv } from 'tailwind-variants';
import { getPawn, type PawnData, pawnsVersion } from '../../state/entities';
import { t } from '../../state/i18n';
import { StatBar } from './StatBar';

const needsPanel = tv({
  slots: {
    root: 'flex flex-col',
    title: [
      'text-center text-rs-gold font-semibold text-[0.8em] mb-[0.85em] tracking-wide',
      'border-b border-rs-stone-light/30 pb-[0.55em]',
    ],
    pawnHeader: [
      'flex items-center gap-[0.8em] mb-[1.1em]',
      'pb-[0.8em] border-b border-rs-stone-light/20',
    ],
    pawnSprite: [
      'w-[2.9em] h-[2.9em]',
      '[image-rendering:pixelated]',
      'border border-rs-stone-light/40 rounded-[0.25em]',
      'bg-rs-stone-dark/50',
    ],
    pawnInfo: 'flex-1',
    pawnName: 'text-[0.95em] font-semibold text-rs-parchment',
    pawnState: 'text-[0.68em] text-rs-parchment-dark uppercase tracking-wide flex gap-[0.3em]',
    statsSection: 'flex flex-col gap-[0.55em]',
    sectionLabel: 'text-[0.62em] uppercase tracking-wider text-rs-parchment-dark mb-[0.4em]',
  },
});

const stateChip = tv({
  base: 'inline-block text-[0.62em] px-[0.3em] py-[0.12em] rounded uppercase tracking-wide mx-[0.08em]',
  variants: {
    state: {
      idle: 'bg-emerald-900/50 text-emerald-400',
      moving: 'bg-indigo-900/50 text-indigo-400',
      working: 'bg-amber-900/50 text-amber-400',
      fighting: 'bg-red-900/50 text-red-400',
      resting: 'bg-blue-900/50 text-blue-400',
      eating: 'bg-orange-900/50 text-orange-400',
      downed: 'bg-slate-900 text-slate-300 border border-slate-600',
      unknown: 'bg-slate-800 text-slate-400',
    },
  },
  defaultVariants: {
    state: 'unknown',
  },
});

type StateKey = keyof typeof stateChip.variants.state;

interface NeedsPanelProps {
  pawn: PawnData;
}

const getPawnStateInfo = (state: number): { text: string; key: StateKey } => {
  switch (state) {
    case PawnState.IDLE:
      return { text: t('ui.states.idle'), key: 'idle' };
    case PawnState.WALKING:
      return { text: t('ui.states.walking'), key: 'moving' };
    case PawnState.WORKING:
      return { text: t('ui.states.working'), key: 'working' };
    case PawnState.FIGHTING:
      return { text: t('ui.states.fighting'), key: 'fighting' };
    case PawnState.FLEEING:
      return { text: t('ui.states.fleeing'), key: 'fighting' };
    case PawnState.SLEEPING:
      return { text: t('ui.states.sleeping'), key: 'resting' };
    default:
      return { text: t('ui.states.unknown'), key: 'unknown' };
  }
};

export const NeedsPanel = ({ pawn }: NeedsPanelProps) => {
  // Subscribe to pawn updates so the panel refreshes while open.
  const _pawnVersion = pawnsVersion.value;
  const livePawn = getPawn({ networkId: pawn.networkId }) ?? pawn;
  const styles = needsPanel();
  const { text: stateText, key: stateKey } = getPawnStateInfo(livePawn.state);
  const isDowned = livePawn.isDowned === 1;

  return (
    <div class={styles.root()}>
      <div class={styles.title()}>{t('ui.panels.pawn')}</div>

      <div class={styles.pawnHeader()}>
        <img
          src="/pawns/front_idle.png"
          alt={livePawn.name}
          class={styles.pawnSprite()}
          draggable={false}
        />
        <div class={styles.pawnInfo()}>
          <div class={styles.pawnName()}>{livePawn.name}</div>
          <div class={styles.pawnState()}>
            {isDowned && (
              <span class={stateChip({ state: 'downed' })}>{t('ui.states.downed')}</span>
            )}
            <span class={stateChip({ state: stateKey })}>{stateText}</span>
          </div>
        </div>
      </div>

      <div class={styles.statsSection()}>
        <div class={styles.sectionLabel()}>{t('ui.labels.vitals')}</div>
        <StatBar
          label={t('ui.labels.health')}
          value={livePawn.health}
          max={livePawn.maxHealth}
          color="health"
        />
        <StatBar label={t('ui.labels.hunger')} value={livePawn.hunger} color="hunger" />
        <StatBar label={t('ui.labels.rest')} value={livePawn.rest} color="rest" />
        <StatBar label={t('ui.labels.mood')} value={livePawn.mood} color="mood" />
      </div>
    </div>
  );
};
